import type { DemoScenarioItem, Locale, SupportedLanguageItem } from './types'

export const DEMO_SCENARIOS_FR: DemoScenarioItem[] = [
  {
    id: 'typescript',
    language: 'typescript',
    languageLabel: 'TypeScript',
    color: '#3178c6',
    title: 'PR #108 : Rendu dynamique des résultats de recherche',
    ticket:
      '[JIRA-382] Afficher dynamiquement les résultats de recherche tapés par l’utilisateur sans rechargement de page.',
    persona: { name: 'Kevin', tag: 'Junior pressé (fait confiance à Copilot)' },
    category: 'Sécurité OWASP (XSS)',
    severity: 'Critique',
    filename: 'src/components/SearchBox.tsx',
    diff: [
      { oldLineNumber: 14, newLineNumber: 14, type: 'context', code: 'export function renderSearchResults(query: string) {' },
      { oldLineNumber: 15, newLineNumber: 15, type: 'context', code: '  const container = document.getElementById("search-output");' },
      { oldLineNumber: 16, newLineNumber: 16, type: 'context', code: '  if (!container) return;' },
      { oldLineNumber: 17, type: 'delete', code: '-  container.textContent = "Recherche en cours...";' },
      { newLineNumber: 17, type: 'add', isVulnerable: true, code: '+  // Injection directe de l’entrée utilisateur dans le DOM' },
      { newLineNumber: 18, type: 'add', isVulnerable: true, code: '+  container.innerHTML = `<div class="result">Requête : ${query}</div>`;' },
      { oldLineNumber: 18, newLineNumber: 19, type: 'context', code: '}' },
    ],
    fixedDiff: [
      { oldLineNumber: 14, newLineNumber: 14, type: 'context', code: 'export function renderSearchResults(query: string) {' },
      { oldLineNumber: 15, newLineNumber: 15, type: 'context', code: '  const container = document.getElementById("search-output");' },
      { oldLineNumber: 16, newLineNumber: 16, type: 'context', code: '  if (!container) return;' },
      { newLineNumber: 17, type: 'add', code: '+  const div = document.createElement("div");' },
      { newLineNumber: 18, type: 'add', code: '+  div.className = "result";' },
      { newLineNumber: 19, type: 'add', code: '+  div.textContent = `Requête : ${query}`; // Échappement natif' },
      { newLineNumber: 20, type: 'add', code: '+  container.replaceChildren(div);' },
      { oldLineNumber: 18, newLineNumber: 21, type: 'context', code: '}' },
    ],
    comment: {
      author: 'Alexandre V.',
      role: 'Staff Engineer & Juge IA',
      avatarInitial: 'A',
      type: 'Vulnérabilité OWASP A03 : Cross-Site Scripting (XSS)',
      text: 'L’utilisation d’`innerHTML` avec une saisie utilisateur non assainie permet à un attaquant d’injecter des balises malveillantes (ex: `<img src=x onerror=stealSession()>`) et de voler les tokens d’authentification. Privilégiez `textContent` ou `replaceChildren`.',
      score: 95,
    },
  },
  {
    id: 'python',
    language: 'python',
    languageLabel: 'Python',
    color: '#3572A5',
    title: 'PR #215 : Récupération du profil membre par identifiant',
    ticket:
      '[JIRA-419] Endpoint REST rapide pour charger les données utilisateur à partir du paramètre d’URL.',
    persona: { name: 'Camille', tag: 'Data & Backend (code vite)' },
    category: 'Sécurité OWASP (SQLi)',
    severity: 'Critique',
    filename: 'backend/services/user_service.py',
    diff: [
      { oldLineNumber: 28, newLineNumber: 28, type: 'context', code: 'def get_user_profile(db_connection, user_id: str):' },
      { oldLineNumber: 29, newLineNumber: 29, type: 'context', code: '    cursor = db_connection.cursor()' },
      { oldLineNumber: 30, type: 'delete', code: '-    # Requête obsolète' },
      { newLineNumber: 30, type: 'add', isVulnerable: true, code: '+    # Concaténation par f-string vulnérable à l’injection' },
      { newLineNumber: 31, type: 'add', isVulnerable: true, code: '+    query = f"SELECT id, username, email FROM users WHERE id = \'{user_id}\'"' },
      { oldLineNumber: 31, newLineNumber: 32, type: 'context', code: '    cursor.execute(query)' },
      { oldLineNumber: 32, newLineNumber: 33, type: 'context', code: '    return cursor.fetchone()' },
    ],
    fixedDiff: [
      { oldLineNumber: 28, newLineNumber: 28, type: 'context', code: 'def get_user_profile(db_connection, user_id: str):' },
      { oldLineNumber: 29, newLineNumber: 29, type: 'context', code: '    cursor = db_connection.cursor()' },
      { newLineNumber: 30, type: 'add', code: '+    query = "SELECT id, username, email FROM users WHERE id = %s"' },
      { newLineNumber: 31, type: 'add', code: '+    cursor.execute(query, (user_id,)) # Requête paramétrée sécurisée' },
      { oldLineNumber: 32, newLineNumber: 32, type: 'context', code: '    return cursor.fetchone()' },
    ],
    comment: {
      author: 'Sarah K.',
      role: 'Lead Sécurité Applicative',
      avatarInitial: 'S',
      type: 'Vulnérabilité OWASP A03 : Injection SQL',
      text: 'L’interpolation de `user_id` via un f-string contourne l’échappement SQL. Un payload comme `\' OR \'1\'=\'1` permet d’exfiltrer la table complète. Utilisez impérativement le tuple de paramètres natif de votre pilote SQL.',
      score: 100,
    },
  },
  {
    id: 'go',
    language: 'go',
    languageLabel: 'Go',
    color: '#00ADD8',
    title: 'PR #77 : Collecte périodique d’événements en streaming',
    ticket:
      '[JIRA-501] Traiter un flux d’événements en tâche de fond pour optimiser la latence HTTP.',
    persona: { name: 'David', tag: 'Fullstack découvrant Go' },
    category: 'Performance & Concurrence',
    severity: 'Élevée',
    filename: 'pkg/collector/worker.go',
    diff: [
      { oldLineNumber: 42, newLineNumber: 42, type: 'context', code: 'func StreamEvents(ctx context.Context, items []string) <-chan Result {' },
      { oldLineNumber: 43, newLineNumber: 43, type: 'context', code: '    out := make(chan Result)' },
      { oldLineNumber: 44, newLineNumber: 44, type: 'context', code: '    go func() {' },
      { oldLineNumber: 45, type: 'delete', code: '-        // pas d’itération concurrente' },
      { newLineNumber: 45, type: 'add', isVulnerable: true, code: '+        for _, item := range items {' },
      { newLineNumber: 46, type: 'add', isVulnerable: true, code: '+            out <- processItem(item) // Bloquant si le récepteur s’arrête !' },
      { newLineNumber: 47, type: 'add', isVulnerable: true, code: '+        }' },
      { oldLineNumber: 46, newLineNumber: 48, type: 'context', code: '    }()' },
      { oldLineNumber: 47, newLineNumber: 49, type: 'context', code: '    return out' },
      { oldLineNumber: 48, newLineNumber: 50, type: 'context', code: '}' },
    ],
    fixedDiff: [
      { oldLineNumber: 42, newLineNumber: 42, type: 'context', code: 'func StreamEvents(ctx context.Context, items []string) <-chan Result {' },
      { oldLineNumber: 43, newLineNumber: 43, type: 'context', code: '    out := make(chan Result, len(items))' },
      { oldLineNumber: 44, newLineNumber: 44, type: 'context', code: '    go func() {' },
      { newLineNumber: 45, type: 'add', code: '+        defer close(out)' },
      { newLineNumber: 46, type: 'add', code: '+        for _, item := range items {' },
      { newLineNumber: 47, type: 'add', code: '+            select {' },
      { newLineNumber: 48, type: 'add', code: '+            case <-ctx.Done(): return // Respect de l’annulation' },
      { newLineNumber: 49, type: 'add', code: '+            case out <- processItem(item):' },
      { newLineNumber: 50, type: 'add', code: '+            }' },
      { newLineNumber: 51, type: 'add', code: '+        }' },
      { oldLineNumber: 46, newLineNumber: 52, type: 'context', code: '    }()' },
      { oldLineNumber: 47, newLineNumber: 53, type: 'context', code: '    return out' },
      { oldLineNumber: 48, newLineNumber: 54, type: 'context', code: '}' },
    ],
    comment: {
      author: 'Marc B.',
      role: 'Staff Backend & Concurrence',
      avatarInitial: 'M',
      type: 'Fuite de ressources : Goroutine Leak',
      text: 'Le canal n’étant pas tamponné et dépourvu de vérification de `ctx.Done()`, la goroutine reste bloquée en mémoire si le consommateur annule la requête. En production sous fort trafic, cela cause une fuite de mémoire fatale.',
      score: 92,
    },
  },
  {
    id: 'rust',
    language: 'rust',
    languageLabel: 'Rust',
    color: '#dea584',
    title: 'PR #133 : Optimisation bas niveau du module de chiffrement',
    ticket:
      '[JIRA-612] Supprimer les allocations intermédiaires lors du parsing du payload cryptographique.',
    persona: { name: 'Théo', tag: 'Junior Rustacean ambitieux' },
    category: 'Architecture & Sûreté mémoire',
    severity: 'Élevée',
    filename: 'src/crypto/vault.rs',
    diff: [
      { oldLineNumber: 88, newLineNumber: 88, type: 'context', code: 'pub fn decrypt_block(ptr: *const u8, len: usize) -> Result<Vec<u8>, CryptError> {' },
      { oldLineNumber: 89, type: 'delete', code: '-    // validation préalable du tampon' },
      { newLineNumber: 89, type: 'add', isVulnerable: true, code: '+    unsafe {' },
      { newLineNumber: 90, type: 'add', isVulnerable: true, code: '+        // Déréférencement aveugle : aucun test sur la nullité du pointeur' },
      { newLineNumber: 91, type: 'add', isVulnerable: true, code: '+        let slice = std::slice::from_raw_parts(ptr, len);' },
      { newLineNumber: 92, type: 'add', isVulnerable: true, code: '+        decrypt_raw(slice)' },
      { newLineNumber: 93, type: 'add', isVulnerable: true, code: '+    }' },
      { oldLineNumber: 90, newLineNumber: 94, type: 'context', code: '}' },
    ],
    fixedDiff: [
      { oldLineNumber: 88, newLineNumber: 88, type: 'context', code: 'pub fn decrypt_block(ptr: *const u8, len: usize) -> Result<Vec<u8>, CryptError> {' },
      { newLineNumber: 89, type: 'add', code: '+    if ptr.is_null() || len == 0 {' },
      { newLineNumber: 90, type: 'add', code: '+        return Err(CryptError::InvalidBuffer);' },
      { newLineNumber: 91, type: 'add', code: '+    }' },
      { newLineNumber: 92, type: 'add', code: '+    unsafe {' },
      { newLineNumber: 93, type: 'add', code: '+        let slice = std::slice::from_raw_parts(ptr, len);' },
      { newLineNumber: 94, type: 'add', code: '+        decrypt_raw(slice)' },
      { newLineNumber: 95, type: 'add', code: '+    }' },
      { oldLineNumber: 90, newLineNumber: 96, type: 'context', code: '}' },
    ],
    comment: {
      author: 'Elena D.',
      role: 'Rust Security Specialist',
      avatarInitial: 'E',
      type: 'Comportement Indéfini (UB) & Crash mémoire',
      text: 'Appeler `from_raw_parts` avec un pointeur nul ou mal aligné constitue un comportement indéfini (Undefined Behavior) immédiat pouvant conduire à une corruption mémoire ou un crash système. Validez toujours `ptr.is_null()`.',
      score: 96,
    },
  },
  {
    id: 'php',
    language: 'php',
    languageLabel: 'PHP',
    color: '#4F5D95',
    title: 'PR #91 : Restauration des préférences de session utilisateur',
    ticket:
      '[JIRA-290] Charger les paramètres enregistrés dans le cookie lors de la reconnexion.',
    persona: { name: 'Lucas', tag: 'Habitué aux anciens patterns PHP' },
    category: 'Sécurité OWASP (Désérialisation)',
    severity: 'Critique',
    filename: 'app/Http/Controllers/SessionController.php',
    diff: [
      { oldLineNumber: 50, newLineNumber: 50, type: 'context', code: 'public function restoreSettings(Request $request) {' },
      { oldLineNumber: 51, newLineNumber: 51, type: 'context', code: '    $cookieData = $request->cookie("user_prefs");' },
      { oldLineNumber: 52, type: 'delete', code: '-    // ancienne méthode statique' },
      { newLineNumber: 52, type: 'add', isVulnerable: true, code: '+    // Désérialisation brute d’une entrée manipulable par le client' },
      { newLineNumber: 53, type: 'add', isVulnerable: true, code: '+    $settings = unserialize(base64_decode($cookieData));' },
      { oldLineNumber: 53, newLineNumber: 54, type: 'context', code: '    return response()->json($settings);' },
      { oldLineNumber: 54, newLineNumber: 55, type: 'context', code: '}' },
    ],
    fixedDiff: [
      { oldLineNumber: 50, newLineNumber: 50, type: 'context', code: 'public function restoreSettings(Request $request) {' },
      { oldLineNumber: 51, newLineNumber: 51, type: 'context', code: '    $cookieData = $request->cookie("user_prefs");' },
      { newLineNumber: 52, type: 'add', code: '+    $settings = json_decode(base64_decode($cookieData), true);' },
      { newLineNumber: 53, type: 'add', code: '+    if (json_last_error() !== JSON_ERROR_NONE) {' },
      { newLineNumber: 54, type: 'add', code: '+        return response()->json(["error" => "Format corrompu"], 400);' },
      { newLineNumber: 55, type: 'add', code: '+    }' },
      { oldLineNumber: 53, newLineNumber: 56, type: 'context', code: '    return response()->json($settings);' },
      { oldLineNumber: 54, newLineNumber: 57, type: 'context', code: '}' },
    ],
    comment: {
      author: 'Claire M.',
      role: 'Consultante Audit & Pentest',
      avatarInitial: 'C',
      type: 'Vulnérabilité OWASP A08 : Insecure Deserialization (RCE)',
      text: 'L’exécution de `unserialize()` sur des cookies forgés par le client permet l’injection d’objets et l’exécution de code arbitraire à distance (RCE) via des classes magiques (`__wakeup`, `__destruct`). Utilisez impérativement `json_decode()`.',
      score: 98,
    },
  },
]

export const DEMO_SCENARIOS_EN: DemoScenarioItem[] = [
  {
    id: 'typescript',
    language: 'typescript',
    languageLabel: 'TypeScript',
    color: '#3178c6',
    title: 'PR #108: Dynamic search results rendering',
    ticket:
      '[JIRA-382] Dynamically render user search input without reloading the page.',
    persona: { name: 'Kevin', tag: 'Rushed junior (trusts Copilot)' },
    category: 'OWASP Security (XSS)',
    severity: 'Critical',
    filename: 'src/components/SearchBox.tsx',
    diff: [
      { oldLineNumber: 14, newLineNumber: 14, type: 'context', code: 'export function renderSearchResults(query: string) {' },
      { oldLineNumber: 15, newLineNumber: 15, type: 'context', code: '  const container = document.getElementById("search-output");' },
      { oldLineNumber: 16, newLineNumber: 16, type: 'context', code: '  if (!container) return;' },
      { oldLineNumber: 17, type: 'delete', code: '-  container.textContent = "Search in progress...";' },
      { newLineNumber: 17, type: 'add', isVulnerable: true, code: '+  // Direct injection of user input into the DOM' },
      { newLineNumber: 18, type: 'add', isVulnerable: true, code: '+  container.innerHTML = `<div class="result">Query: ${query}</div>`;' },
      { oldLineNumber: 18, newLineNumber: 19, type: 'context', code: '}' },
    ],
    fixedDiff: [
      { oldLineNumber: 14, newLineNumber: 14, type: 'context', code: 'export function renderSearchResults(query: string) {' },
      { oldLineNumber: 15, newLineNumber: 15, type: 'context', code: '  const container = document.getElementById("search-output");' },
      { oldLineNumber: 16, newLineNumber: 16, type: 'context', code: '  if (!container) return;' },
      { newLineNumber: 17, type: 'add', code: '+  const div = document.createElement("div");' },
      { newLineNumber: 18, type: 'add', code: '+  div.className = "result";' },
      { newLineNumber: 19, type: 'add', code: '+  div.textContent = `Query: ${query}`; // Native escaping' },
      { newLineNumber: 20, type: 'add', code: '+  container.replaceChildren(div);' },
      { oldLineNumber: 18, newLineNumber: 21, type: 'context', code: '}' },
    ],
    comment: {
      author: 'Alexandre V.',
      role: 'Staff Engineer & AI Judge',
      avatarInitial: 'A',
      type: 'OWASP A03 Vulnerability: Cross-Site Scripting (XSS)',
      text: 'Using `innerHTML` with unsanitized user input allows an attacker to inject malicious HTML/JS tags (e.g. `<img src=x onerror=stealSession()>`) and hijack authentication tokens. Prefer `textContent` or `replaceChildren`.',
      score: 95,
    },
  },
  {
    id: 'python',
    language: 'python',
    languageLabel: 'Python',
    color: '#3572A5',
    title: 'PR #215: Fetch member profile by identifier',
    ticket:
      '[JIRA-419] Quick REST endpoint to load user profile data directly from URL parameter.',
    persona: { name: 'Camille', tag: 'Data & Backend (rapid coder)' },
    category: 'OWASP Security (SQLi)',
    severity: 'Critical',
    filename: 'backend/services/user_service.py',
    diff: [
      { oldLineNumber: 28, newLineNumber: 28, type: 'context', code: 'def get_user_profile(db_connection, user_id: str):' },
      { oldLineNumber: 29, newLineNumber: 29, type: 'context', code: '    cursor = db_connection.cursor()' },
      { oldLineNumber: 30, type: 'delete', code: '-    # Obsolete query' },
      { newLineNumber: 30, type: 'add', isVulnerable: true, code: '+    # f-string interpolation vulnerable to injection' },
      { newLineNumber: 31, type: 'add', isVulnerable: true, code: '+    query = f"SELECT id, username, email FROM users WHERE id = \'{user_id}\'"' },
      { oldLineNumber: 31, newLineNumber: 32, type: 'context', code: '    cursor.execute(query)' },
      { oldLineNumber: 32, newLineNumber: 33, type: 'context', code: '    return cursor.fetchone()' },
    ],
    fixedDiff: [
      { oldLineNumber: 28, newLineNumber: 28, type: 'context', code: 'def get_user_profile(db_connection, user_id: str):' },
      { oldLineNumber: 29, newLineNumber: 29, type: 'context', code: '    cursor = db_connection.cursor()' },
      { newLineNumber: 30, type: 'add', code: '+    query = "SELECT id, username, email FROM users WHERE id = %s"' },
      { newLineNumber: 31, type: 'add', code: '+    cursor.execute(query, (user_id,)) # Secure parameterized query' },
      { oldLineNumber: 32, newLineNumber: 32, type: 'context', code: '    return cursor.fetchone()' },
    ],
    comment: {
      author: 'Sarah K.',
      role: 'Lead Application Security',
      avatarInitial: 'S',
      type: 'OWASP A03 Vulnerability: SQL Injection',
      text: 'Interpolating `user_id` with an f-string bypasses SQL parameter escaping. A payload such as `\' OR \'1\'=\'1` allows dumping the whole table. Always use your database driver\'s native parameter tuple.',
      score: 100,
    },
  },
  {
    id: 'go',
    language: 'go',
    languageLabel: 'Go',
    color: '#00ADD8',
    title: 'PR #77: Periodic streaming event collection',
    ticket:
      '[JIRA-501] Process background event stream to optimize HTTP response latency.',
    persona: { name: 'David', tag: 'Fullstack discovering Go' },
    category: 'Performance & Concurrency',
    severity: 'High',
    filename: 'pkg/collector/worker.go',
    diff: [
      { oldLineNumber: 42, newLineNumber: 42, type: 'context', code: 'func StreamEvents(ctx context.Context, items []string) <-chan Result {' },
      { oldLineNumber: 43, newLineNumber: 43, type: 'context', code: '    out := make(chan Result)' },
      { oldLineNumber: 44, newLineNumber: 44, type: 'context', code: '    go func() {' },
      { oldLineNumber: 45, type: 'delete', code: '-        // no concurrent iteration' },
      { newLineNumber: 45, type: 'add', isVulnerable: true, code: '+        for _, item := range items {' },
      { newLineNumber: 46, type: 'add', isVulnerable: true, code: '+            out <- processItem(item) // Blocks indefinitely if receiver drops!' },
      { newLineNumber: 47, type: 'add', isVulnerable: true, code: '+        }' },
      { oldLineNumber: 46, newLineNumber: 48, type: 'context', code: '    }()' },
      { oldLineNumber: 47, newLineNumber: 49, type: 'context', code: '    return out' },
      { oldLineNumber: 48, newLineNumber: 50, type: 'context', code: '}' },
    ],
    fixedDiff: [
      { oldLineNumber: 42, newLineNumber: 42, type: 'context', code: 'func StreamEvents(ctx context.Context, items []string) <-chan Result {' },
      { oldLineNumber: 43, newLineNumber: 43, type: 'context', code: '    out := make(chan Result, len(items))' },
      { oldLineNumber: 44, newLineNumber: 44, type: 'context', code: '    go func() {' },
      { newLineNumber: 45, type: 'add', code: '+        defer close(out)' },
      { newLineNumber: 46, type: 'add', code: '+        for _, item := range items {' },
      { newLineNumber: 47, type: 'add', code: '+            select {' },
      { newLineNumber: 48, type: 'add', code: '+            case <-ctx.Done(): return // Honors context cancellation' },
      { newLineNumber: 49, type: 'add', code: '+            case out <- processItem(item):' },
      { newLineNumber: 50, type: 'add', code: '+            }' },
      { newLineNumber: 51, type: 'add', code: '+        }' },
      { oldLineNumber: 46, newLineNumber: 52, type: 'context', code: '    }()' },
      { oldLineNumber: 47, newLineNumber: 53, type: 'context', code: '    return out' },
      { oldLineNumber: 48, newLineNumber: 54, type: 'context', code: '}' },
    ],
    comment: {
      author: 'Marc B.',
      role: 'Staff Backend & Concurrency',
      avatarInitial: 'M',
      type: 'Resource Leak: Goroutine Leak',
      text: 'Because the channel is unbuffered and has no `ctx.Done()` branch, the spawned goroutine stays pinned in memory if the consumer aborts the request. Under high traffic, this causes a fatal memory leak.',
      score: 92,
    },
  },
  {
    id: 'rust',
    language: 'rust',
    languageLabel: 'Rust',
    color: '#dea584',
    title: 'PR #133: Low-level crypto module optimization',
    ticket:
      '[JIRA-612] Eliminate intermediate heap allocations during cryptographic payload parsing.',
    persona: { name: 'Théo', tag: 'Ambitious Junior Rustacean' },
    category: 'Architecture & Memory Safety',
    severity: 'High',
    filename: 'src/crypto/vault.rs',
    diff: [
      { oldLineNumber: 88, newLineNumber: 88, type: 'context', code: 'pub fn decrypt_block(ptr: *const u8, len: usize) -> Result<Vec<u8>, CryptError> {' },
      { oldLineNumber: 89, type: 'delete', code: '-    // prior buffer validation' },
      { newLineNumber: 89, type: 'add', isVulnerable: true, code: '+    unsafe {' },
      { newLineNumber: 90, type: 'add', isVulnerable: true, code: '+        // Blind dereference: no null check on raw pointer' },
      { newLineNumber: 91, type: 'add', isVulnerable: true, code: '+        let slice = std::slice::from_raw_parts(ptr, len);' },
      { newLineNumber: 92, type: 'add', isVulnerable: true, code: '+        decrypt_raw(slice)' },
      { newLineNumber: 93, type: 'add', isVulnerable: true, code: '+    }' },
      { oldLineNumber: 90, newLineNumber: 94, type: 'context', code: '}' },
    ],
    fixedDiff: [
      { oldLineNumber: 88, newLineNumber: 88, type: 'context', code: 'pub fn decrypt_block(ptr: *const u8, len: usize) -> Result<Vec<u8>, CryptError> {' },
      { newLineNumber: 89, type: 'add', code: '+    if ptr.is_null() || len == 0 {' },
      { newLineNumber: 90, type: 'add', code: '+        return Err(CryptError::InvalidBuffer);' },
      { newLineNumber: 91, type: 'add', code: '+    }' },
      { newLineNumber: 92, type: 'add', code: '+    unsafe {' },
      { newLineNumber: 93, type: 'add', code: '+        let slice = std::slice::from_raw_parts(ptr, len);' },
      { newLineNumber: 94, type: 'add', code: '+        decrypt_raw(slice)' },
      { newLineNumber: 95, type: 'add', code: '+    }' },
      { oldLineNumber: 90, newLineNumber: 96, type: 'context', code: '}' },
    ],
    comment: {
      author: 'Elena D.',
      role: 'Rust Security Specialist',
      avatarInitial: 'E',
      type: 'Undefined Behavior (UB) & Memory Crash',
      text: 'Invoking `from_raw_parts` with a null or misaligned pointer immediately triggers Undefined Behavior (UB), leading to memory corruption or crashes. Always validate `ptr.is_null()`.',
      score: 96,
    },
  },
  {
    id: 'php',
    language: 'php',
    languageLabel: 'PHP',
    color: '#4F5D95',
    title: 'PR #91: User session preference restoration',
    ticket:
      '[JIRA-290] Load settings saved in cookie upon user sign-in.',
    persona: { name: 'Lucas', tag: 'Used to legacy PHP patterns' },
    category: 'OWASP Security (Deserialization)',
    severity: 'Critical',
    filename: 'app/Http/Controllers/SessionController.php',
    diff: [
      { oldLineNumber: 50, newLineNumber: 50, type: 'context', code: 'public function restoreSettings(Request $request) {' },
      { oldLineNumber: 51, newLineNumber: 51, type: 'context', code: '    $cookieData = $request->cookie("user_prefs");' },
      { oldLineNumber: 52, type: 'delete', code: '-    // old static method' },
      { newLineNumber: 52, type: 'add', isVulnerable: true, code: '+    // Raw deserialization of client-controllable input' },
      { newLineNumber: 53, type: 'add', isVulnerable: true, code: '+    $settings = unserialize(base64_decode($cookieData));' },
      { oldLineNumber: 53, newLineNumber: 54, type: 'context', code: '    return response()->json($settings);' },
      { oldLineNumber: 54, newLineNumber: 55, type: 'context', code: '}' },
    ],
    fixedDiff: [
      { oldLineNumber: 50, newLineNumber: 50, type: 'context', code: 'public function restoreSettings(Request $request) {' },
      { oldLineNumber: 51, newLineNumber: 51, type: 'context', code: '    $cookieData = $request->cookie("user_prefs");' },
      { newLineNumber: 52, type: 'add', code: '+    $settings = json_decode(base64_decode($cookieData), true);' },
      { newLineNumber: 53, type: 'add', code: '+    if (json_last_error() !== JSON_ERROR_NONE) {' },
      { newLineNumber: 54, type: 'add', code: '+        return response()->json(["error" => "Corrupted format"], 400);' },
      { newLineNumber: 55, type: 'add', code: '+    }' },
      { oldLineNumber: 53, newLineNumber: 56, type: 'context', code: '    return response()->json($settings);' },
      { oldLineNumber: 54, newLineNumber: 57, type: 'context', code: '}' },
    ],
    comment: {
      author: 'Claire M.',
      role: 'Penetration Testing Consultant',
      avatarInitial: 'C',
      type: 'OWASP A08 Vulnerability: Insecure Deserialization (RCE)',
      text: 'Executing `unserialize()` on client-crafted cookies allows arbitrary object injection and Remote Code Execution (RCE) via magic methods (`__wakeup`, `__destruct`). Always use `json_decode()`.',
      score: 98,
    },
  },
]

export const SUPPORTED_LANGUAGES_FR: SupportedLanguageItem[] = [
  {
    name: 'TypeScript',
    tag: 'Web & Node.js',
    color: '#3178c6',
    flaws: 'XSS, fuites mémoire React hooks, contournement JWT, pièges async',
    level: 'Junior → Senior',
  },
  {
    name: 'Python',
    tag: 'Backend & Data',
    color: '#3572A5',
    flaws: 'Injections SQL, race conditions FastAPI, pickle RCE, mutables globaux',
    level: 'Junior → Senior',
  },
  {
    name: 'Go',
    tag: 'Microservices & Cloud',
    color: '#00ADD8',
    flaws: 'Goroutine leaks, nil pointer panics, channel deadlocks, secrets en dur',
    level: 'Intermédiaire → Senior',
  },
  {
    name: 'Rust',
    tag: 'Système & Performance',
    color: '#dea584',
    flaws: 'Unsafe blocks injustifiés, bugs de lifetime, panics unwrap() en prod',
    level: 'Intermédiaire → Senior',
  },
  {
    name: 'Java & C#',
    tag: 'Enterprise & APIs',
    color: '#b07219',
    flaws: 'SSRF, désérialisation XML/binaire, saturation de pool de connexions',
    level: 'Junior → Senior',
  },
  {
    name: 'PHP',
    tag: 'Modern Web Backend',
    color: '#4F5D95',
    flaws: 'Insecure deserialization, comparaison faible ==, failles upload',
    level: 'Junior → Intermédiaire',
  },
]

export const SUPPORTED_LANGUAGES_EN: SupportedLanguageItem[] = [
  {
    name: 'TypeScript',
    tag: 'Web & Node.js',
    color: '#3178c6',
    flaws: 'XSS, React hooks memory leaks, JWT bypass, async pitfalls',
    level: 'Junior → Senior',
  },
  {
    name: 'Python',
    tag: 'Backend & Data',
    color: '#3572A5',
    flaws: 'SQL injections, FastAPI race conditions, pickle RCE, mutable globals',
    level: 'Junior → Senior',
  },
  {
    name: 'Go',
    tag: 'Microservices & Cloud',
    color: '#00ADD8',
    flaws: 'Goroutine leaks, nil pointer panics, channel deadlocks, hardcoded secrets',
    level: 'Intermediate → Senior',
  },
  {
    name: 'Rust',
    tag: 'System & Performance',
    color: '#dea584',
    flaws: 'Unjustified unsafe blocks, lifetime bugs, production unwrap() panics',
    level: 'Intermediate → Senior',
  },
  {
    name: 'Java & C#',
    tag: 'Enterprise & APIs',
    color: '#b07219',
    flaws: 'SSRF, XML/binary deserialization, connection pool exhaustion',
    level: 'Junior → Senior',
  },
  {
    name: 'PHP',
    tag: 'Modern Web Backend',
    color: '#4F5D95',
    flaws: 'Insecure deserialization, loose == comparison, file upload vulnerabilities',
    level: 'Junior → Intermediate',
  },
]

export function getDemoScenarios(locale: Locale): DemoScenarioItem[] {
  return locale === 'en' ? DEMO_SCENARIOS_EN : DEMO_SCENARIOS_FR
}

export function getSupportedLanguages(locale: Locale): SupportedLanguageItem[] {
  return locale === 'en' ? SUPPORTED_LANGUAGES_EN : SUPPORTED_LANGUAGES_FR
}
